import React, { useEffect, useState } from "react";
import {
  Table,
  Card,
  Row,
  Col,
  Statistic,
  Spin,
  Alert,
  Tag,
  Badge,
  Button,
  Tooltip,
  Input,
  Select,
  Space,
} from "antd";
import {
  CrownOutlined,
  ReloadOutlined,
  SearchOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { getPremiumSlotsStatus } from "../../services/professionService";
import { toast } from "react-toastify";

const { Search } = Input;
const { Option } = Select;

// ==============================================
// CONSTANTES DE COULEUR
// ==============================================
const COLORS = {
  primary: "#4bb0c0",
  secondary: "#f4a261",
  light: "#f8f9fa",
  dark: "#343a40",
  white: "#ffffff",
  danger: "#dc3545",
  success: "#4bb0c0",
};

// ==============================================
// TYPES
// ==============================================

interface PremiumUser {
  id: number;
  firstname: string;
  name: string;
  email: string;
  company?: string;
  premium_position: number;
  premium_purchased_at: string;
  premium_expires_at: string;
}

interface ProfessionPremiumStatus {
  professionId: number;
  professionName: string;
  maxSlots: number;
  currentPremiumCount: number;
  availableSlots: number;
  price: number;
  premiumUsers: PremiumUser[];
}

// ==============================================
// COMPOSANT PRINCIPAL
// ==============================================

const PremiumDashboard: React.FC = () => {
  const [data, setData] = useState<ProfessionPremiumStatus[]>([]);
  const [filteredData, setFilteredData] = useState<ProfessionPremiumStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // États pour les filtres
  const [searchText, setSearchText] = useState("");
  const [occupancyFilter, setOccupancyFilter] = useState<string>("all");
  const [priceFilter, setPriceFilter] = useState<string>("all");

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [data, searchText, occupancyFilter, priceFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const status = await getPremiumSlotsStatus();
      setData(status);
      setFilteredData(status);
    } catch (error) {
      toast.error("Erreur lors du chargement du dashboard premium");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success("Dashboard mis à jour");
  };

  // Fonction d'application des filtres
  const applyFilters = () => {
    let filtered = [...data];

    // Filtre par recherche (nom de profession)
    if (searchText) {
      filtered = filtered.filter(item =>
        item.professionName.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Filtre par taux d'occupation
    if (occupancyFilter !== "all") {
      filtered = filtered.filter(item => {
        const occupancyRate = (item.currentPremiumCount / item.maxSlots) * 100;
        
        switch (occupancyFilter) {
          case "available":
            return item.availableSlots > 0;
          case "full":
            return item.availableSlots === 0;
          case "low":
            return occupancyRate < 30;
          case "medium":
            return occupancyRate >= 30 && occupancyRate < 70;
          case "high":
            return occupancyRate >= 70 && occupancyRate < 100;
          default:
            return true;
        }
      });
    }

    // Filtre par prix
    if (priceFilter !== "all") {
      filtered = filtered.filter(item => {
        switch (priceFilter) {
          case "free":
            return !item.price || item.price <= 0;
          case "low":
            return item.price > 0 && item.price < 200;
          case "medium":
            return item.price >= 200 && item.price < 400;
          case "high":
            return item.price >= 400;
          default:
            return true;
        }
      });
    }

    setFilteredData(filtered);
  };

  // Reset tous les filtres
  const resetFilters = () => {
    setSearchText("");
    setOccupancyFilter("all");
    setPriceFilter("all");
  };

  // Calcul des statistiques globales sur les données filtrées
  const totalStats = filteredData.reduce(
    (acc, profession) => {
      acc.totalSlots += profession.maxSlots;
      acc.occupiedSlots += profession.currentPremiumCount;
      acc.availableSlots += profession.availableSlots;
      acc.totalRevenue +=
        profession.currentPremiumCount * (profession.price || 0);
      return acc;
    },
    { totalSlots: 0, occupiedSlots: 0, availableSlots: 0, totalRevenue: 0 }
  );

  const columns = [
    {
      title: "Profession",
      dataIndex: "professionName",
      key: "professionName",
      width: 200,
      fixed: "left" as const,
      sorter: (a: ProfessionPremiumStatus, b: ProfessionPremiumStatus) => 
        a.professionName.localeCompare(b.professionName),
      render: (text: string, record: ProfessionPremiumStatus) => (
        <span style={{ fontWeight: 500 }}>
          {text}
          {record.price > 0 && (
            <Tag color="gold" style={{ marginLeft: 8 }}>
              {record.price}€/an
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: "Places",
      key: "slots",
      width: 150,
      sorter: (a: ProfessionPremiumStatus, b: ProfessionPremiumStatus) => 
        (a.currentPremiumCount / a.maxSlots) - (b.currentPremiumCount / b.maxSlots),
      render: (_: any, record: ProfessionPremiumStatus) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Badge
            count={`${record.currentPremiumCount}/${record.maxSlots}`}
            style={{
              backgroundColor:
                record.availableSlots === 0
                  ? COLORS.danger
                  : record.availableSlots < 3
                  ? COLORS.secondary
                  : COLORS.success,
              color: COLORS.white,
            }}
          />
          <span style={{ fontSize: 12, color: "#666" }}>
            {record.availableSlots} libres
          </span>
        </div>
      ),
    },
    {
      title: "Positions premium",
      key: "positions",
      width: 180,
      render: (_: any, record: ProfessionPremiumStatus) => {
        const occupiedCount = record.premiumUsers.length;
        
        return (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <CrownOutlined style={{ color: "#faad14", fontSize: 16 }} />
              <span style={{ fontWeight: 500 }}>{occupiedCount}/{record.maxSlots}</span>
              <Tag color={occupiedCount === record.maxSlots ? "red" : occupiedCount > record.maxSlots/2 ? "orange" : "green"}>
                {occupiedCount === record.maxSlots ? "Complet" : `${record.maxSlots - occupiedCount} libre`}
              </Tag>
            </div>
            
            {occupiedCount > 0 && (
              <div style={{ fontSize: 12, color: "#595959", maxHeight: 60, overflow: "hidden" }}>
                {record.premiumUsers.map((user, index) => (
                  <Tooltip key={user.id} title={`#${user.premium_position} - ${user.firstname} ${user.name}`}>
                    <span style={{ 
                      display: "inline-block", 
                      background: "#fff7e6", 
                      padding: "2px 6px", 
                      borderRadius: 12,
                      margin: "0 4px 4px 0",
                      fontSize: 11
                    }}>
                      #{user.premium_position}
                    </span>
                  </Tooltip>
                ))}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "CA annuel",
      key: "revenue",
      width: 150,
      sorter: (a: ProfessionPremiumStatus, b: ProfessionPremiumStatus) => 
        (a.currentPremiumCount * (a.price || 0)) - (b.currentPremiumCount * (b.price || 0)),
      render: (_: any, record: ProfessionPremiumStatus) => (
        <span style={{ fontWeight: 600, color: COLORS.primary }}>
          {(record.currentPremiumCount * (record.price || 0)).toLocaleString()} €
        </span>
      ),
    },
  ];

  if (loading && data.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: 60 }}>
        <Spin size="large" />
        <p style={{ marginTop: 20, color: COLORS.dark }}>
          Chargement du dashboard premium...
        </p>
      </div>
    );
  }

  return (
    <div className="premium-dashboard">
      {/* En-tête avec bouton refresh */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <h2 style={{ margin: 0, color: COLORS.dark }}>
            <CrownOutlined style={{ color: COLORS.secondary, marginRight: 8 }} />
            Dashboard Premium
          </h2>
          <p style={{ margin: 0, color: "#666", fontSize: 14 }}>
            Suivi des positions premium par profession
          </p>
        </div>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          loading={refreshing}
          style={{
            borderColor: COLORS.primary,
            color: COLORS.primary,
          }}
        >
          Actualiser
        </Button>
      </div>

      {/* Barre de recherche et filtres */}
      <Card style={{ marginBottom: 24, backgroundColor: COLORS.light }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Search
              placeholder="Rechercher une profession..."
              allowClear
              enterButton={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={setSearchText}
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              style={{ width: "100%" }}
              placeholder="Filtrer par disponibilité"
              value={occupancyFilter}
              onChange={setOccupancyFilter}
              allowClear
            >
              <Option value="all">Toutes les professions</Option>
              <Option value="available">Places disponibles</Option>
              <Option value="full">Complet</Option>
              <Option value="low">Faible occupation (&lt;30%)</Option>
              <Option value="medium">Occupation moyenne (30-70%)</Option>
              <Option value="high">Forte occupation (70-99%)</Option>
            </Select>
          </Col>
          <Col xs={24} md={6}>
            <Select
              style={{ width: "100%" }}
              placeholder="Filtrer par prix"
              value={priceFilter}
              onChange={setPriceFilter}
              allowClear
            >
              <Option value="all">Tous les prix</Option>
              <Option value="free">Gratuit</Option>
              <Option value="low">&lt; 200€</Option>
              <Option value="medium">200€ - 400€</Option>
              <Option value="high">400€ et +</Option>
            </Select>
          </Col>
          <Col xs={24} md={4}>
            <Button 
              onClick={resetFilters} 
              block
              icon={<FilterOutlined />}
            >
              Réinitialiser
            </Button>
          </Col>
        </Row>
        
        {/* Indicateur de résultats */}
        <div style={{ marginTop: 12, fontSize: 13, color: "#666" }}>
          <FilterOutlined style={{ marginRight: 6 }} />
          {filteredData.length} profession(s) sur {data.length} affichée(s)
          {(searchText || occupancyFilter !== "all" || priceFilter !== "all") && (
            <Button 
              type="link" 
              onClick={resetFilters} 
              size="small"
              style={{ marginLeft: 12, padding: 0 }}
            >
              Effacer tous les filtres
            </Button>
          )}
        </div>
      </Card>

      {/* Statistiques globales sur les données filtrées */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card
            style={{
              backgroundColor: COLORS.light,
              borderRadius: 8,
              borderLeft: `4px solid ${COLORS.primary}`,
            }}
          >
            <Statistic
              title="Places premium totales"
              value={totalStats.totalSlots}
              suffix="places"
              valueStyle={{ color: COLORS.primary }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card
            style={{
              backgroundColor: COLORS.light,
              borderRadius: 8,
              borderLeft: `4px solid ${COLORS.success}`,
            }}
          >
            <Statistic
              title="Places occupées"
              value={totalStats.occupiedSlots}
              suffix={`/${totalStats.totalSlots}`}
              valueStyle={{ color: COLORS.success }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card
            style={{
              backgroundColor: COLORS.light,
              borderRadius: 8,
              borderLeft: `4px solid ${COLORS.secondary}`,
            }}
          >
            <Statistic
              title="Places disponibles"
              value={totalStats.availableSlots}
              suffix="places"
              valueStyle={{ color: COLORS.secondary }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card
            style={{
              backgroundColor: COLORS.light,
              borderRadius: 8,
              borderLeft: `4px solid #faad14`,
            }}
          >
            <Statistic
              title="CA annuel estimé"
              value={totalStats.totalRevenue}
              precision={0}
              suffix="€"
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Tableau des professions avec en-têtes fixes */}
      {filteredData.length === 0 ? (
        <Alert
          message="Aucune donnée premium"
          description={
            data.length > 0 
              ? "Aucune profession ne correspond aux filtres sélectionnés." 
              : "Aucune profession n'a de tarif premium configuré."
          }
          type="info"
          showIcon
          action={
            (searchText || occupancyFilter !== "all" || priceFilter !== "all") && (
              <Button size="small" onClick={resetFilters}>
                Réinitialiser les filtres
              </Button>
            )
          }
          style={{ backgroundColor: COLORS.light, borderColor: COLORS.primary }}
        />
      ) : (
        <div
          style={{
            maxHeight: "70vh",
            overflowY: "auto",
            overflowX: "auto",
            border: "1px solid #dee2e6",
            borderRadius: "8px",
            position: "relative",
          }}
        >
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey="professionId"
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `${total} professions`,
            }}
            bordered
            scroll={{ x: 1000, y: "calc(70vh - 200px)" }}
            style={{
              borderCollapse: "separate",
              borderSpacing: 0,
            }}
            components={{
              header: {
                cell: (props: any) => (
                  <th
                    {...props}
                    style={{
                      ...props.style,
                      position: "sticky",
                      top: 0,
                      zIndex: 100,
                      backgroundColor: "#fafafa",
                      borderBottom: "2px solid #f0f0f0",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    }}
                  />
                ),
              },
            }}
            expandable={{
              expandedRowRender: (record) => (
                <div style={{ padding: "12px 24px" }}>
                  <h4 style={{ marginBottom: 16, color: COLORS.dark }}>
                    Détail des positions premium
                  </h4>
                  {record.premiumUsers.length === 0 ? (
                    <Alert
                      message="Aucun premium"
                      description={`Aucune position premium occupée pour ${record.professionName}`}
                      type="info"
                      showIcon
                    />
                  ) : (
                    <Table
                      dataSource={record.premiumUsers}
                      rowKey="id"
                      pagination={false}
                      size="small"
                      scroll={{ x: 800 }}
                    >
                      <Table.Column
                        title="Position"
                        dataIndex="premium_position"
                        key="position"
                        width={100}
                        render={(pos: number) => (
                          <Tag color="gold" style={{ fontWeight: 600 }}>
                            #{pos}
                          </Tag>
                        )}
                      />
                      <Table.Column
                        title="Professionnel"
                        key="name"
                        render={(_: any, user: PremiumUser) => (
                          <span style={{ fontWeight: 500 }}>
                            {user.firstname} {user.name}
                          </span>
                        )}
                      />
                      <Table.Column
                        title="Entreprise"
                        dataIndex="company"
                        key="company"
                      />
                      <Table.Column
                        title="Email"
                        dataIndex="email"
                        key="email"
                      />
                      <Table.Column
                        title="Date d'achat"
                        dataIndex="premium_purchased_at"
                        key="purchaseDate"
                        render={(date: string) =>
                          date ? new Date(date).toLocaleDateString("fr-FR") : "-"
                        }
                      />
                      <Table.Column
                        title="Expiration"
                        dataIndex="premium_expires_at"
                        key="expiresAt"
                        render={(date: string) => {
                          const isExpired = new Date(date) < new Date();
                          return (
                            <Tag color={isExpired ? "red" : "green"}>
                              {date ? new Date(date).toLocaleDateString("fr-FR") : "-"}
                            </Tag>
                          );
                        }}
                      />
                    </Table>
                  )}
                </div>
              ),
              expandRowByClick: true,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default PremiumDashboard;